import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar } from "recharts";
import { useAuth } from "react-oidc-context";
import { Sidebar } from "../../components/Sidebar";
import { useStatsHotels } from "../../components/GreenMiles/hooks/useStatsHotels";
import { STATS_FEATURE_FILTERS } from "../../components/GreenMiles/utils/featureHotelFilters";
import FeatureStatusFilter from "../../components/GreenMiles/Stats/FeatureStatusFilter";
import FeatureStatusBadge from "../../components/Home/FeatureStatusBadge";
import { LoadingState, ErrorState } from "../../components/UI";

const API_URL = process.env.REACT_APP_API_URL || "/api";

const percentageFormatter = new Intl.NumberFormat("fr-FR", {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

const monthFormatter = new Intl.DateTimeFormat("fr-FR", {
  month: "short",
  year: "numeric",
});

const dayFormatter = new Intl.DateTimeFormat("fr-FR", {
  day: "2-digit",
  month: "short",
  year: "numeric",
});

const formatDateInput = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const parseDateInput = (value) => {
  const [year, month, day] = value.split("-").map((part) => parseInt(part, 10));
  return new Date(year, month - 1, day);
};

const DEFAULT_DATE_RANGE = (() => {
  const today = new Date();
  const endDate = formatDateInput(today);
  const startDate = formatDateInput(new Date(today.getFullYear() - 1, today.getMonth(), today.getDate()));
  return { startDate, endDate };
})();

const getLastMonthsRange = (monthsCount) => {
  const today = new Date();
  return {
    startDate: formatDateInput(new Date(today.getFullYear(), today.getMonth() - monthsCount, today.getDate())),
    endDate: formatDateInput(today),
  };
};

const formatNumber = (value, maximumFractionDigits = 0) =>
  new Intl.NumberFormat("fr-FR", { maximumFractionDigits }).format(Number(value || 0));

// Fonction pour arrondir uniquement les valeurs avec problèmes de précision flottante (ex: 481.999... -> 482)
const roundFloatPrecision = (value) => {
  if (!value || value === 0) return value;
  const rounded = Math.round(value);
  // Si la valeur est très proche d'un entier (à moins de 0.0001), arrondir à l'entier supérieur
  if (Math.abs(value - rounded) < 0.0001) {
    return Math.ceil(value);
  }
  return value;
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) {
    return null;
  }

  const tooltipData = payload[0]?.payload?.tooltip;
  if (!tooltipData) {
    return null;
  }

  const gm = Math.round(tooltipData.gm || 0);
  const real = Math.round(tooltipData.real || 0);
  const actionsCount = Math.round(tooltipData.actionsCount || 0);
  const rewardsCount = Math.round(tooltipData.rewardsCount || 0);

  const titles = {
    gm: "Séjours GreenMiles",
    actions: "Séjours GM avec action",
    effectiveActions: "Séjours GM avec action efficace",
    rewards: "Séjours GM avec récompense",
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-4 space-y-2">
      <div className="text-sm font-semibold text-gray-900 mb-3">{tooltipData.range || label}</div>
      {payload.map((entry, index) => {
        const name = entry.dataKey;
        const value = entry.value || 0;
        const percentage = percentageFormatter.format(value);
        let details = "";

        if (name === "gm") {
          details = `(${gm}/${real}) ${gm} = nb de séjours et ${real} nb séjours potentiel`;
        } else if (name === "actions") {
          details = `(${actionsCount}/${gm}) ${actionsCount} = nb de séjours avec action et ${gm} nb séjours GM`;
        } else if (name === "effectiveActions") {
          const effectiveCount = Math.round(tooltipData.effectiveActionsCount || 0);
          details = `(${effectiveCount}/${gm}) ${effectiveCount} = nb de séjours avec action efficace (€ > 0) et ${gm} nb séjours GM`;
        } else if (name === "rewards") {
          details = `(${rewardsCount}/${gm}) ${rewardsCount} = nb de séjours avec récompense et ${gm} nb séjours GM`;
        }

        return (
          <div key={index} className="text-sm">
            <div className="flex items-center gap-2">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: entry.color || "#000" }}
              />
              <span className="font-medium text-gray-800">{titles[name] || name} :</span>
              <span className="font-semibold text-gray-900">{percentage}%</span>
            </div>
            {details && <div className="text-xs text-gray-600 ml-5 mt-1">{details}</div>}
          </div>
        );
      })}
    </div>
  );
};

const cloneDate = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const getStartOfISOWeek = (date) => {
  const result = cloneDate(date);
  const day = result.getDay() || 7;
  if (day !== 1) {
    result.setDate(result.getDate() - (day - 1));
  }
  return result;
};

const getEndOfISOWeek = (date) => {
  const result = getStartOfISOWeek(date);
  result.setDate(result.getDate() + 6);
  return result;
};

const minDate = (a, b) => (a.getTime() <= b.getTime() ? cloneDate(a) : cloneDate(b));
const maxDate = (a, b) => (a.getTime() >= b.getTime() ? cloneDate(a) : cloneDate(b));

const hasMeaningfulTimeline = (timeline) => {
  if (!timeline || !Array.isArray(timeline.buckets)) {
    return false;
  }
  return timeline.buckets.some((bucket) => {
    const gm = Number(bucket?.gmStaysPercentage || 0);
    const actions = Number(bucket?.staysWithActionsPercentage || 0);
    const effectiveActions = Number(bucket?.staysWithEffectiveActionsPercentage || 0);
    const rewards = Number(bucket?.staysWithRewardsPercentage || 0);
    return gm > 0 || actions > 0 || effectiveActions > 0 || rewards > 0;
  });
};

const buildChartData = (timeline) => {
  if (!timeline || !Array.isArray(timeline.buckets)) {
    return [];
  }

  return timeline.buckets.map((bucket) => {
    const start = parseDateInput(bucket.bucketStart);
    const end = parseDateInput(bucket.bucketEnd);

    let label = monthFormatter.format(start);
    if (timeline.granularity === "day") {
      label = dayFormatter.format(start);
    } else if (timeline.granularity === "week") {
      label = `${dayFormatter.format(start)} → ${dayFormatter.format(end)}`;
    }

    return {
      label,
      gm: Number(bucket.gmStaysPercentage || 0),
      actions: Number(bucket.staysWithActionsPercentage || 0),
      effectiveActions: Number(bucket.staysWithEffectiveActionsPercentage || 0),
      rewards: Number(bucket.staysWithRewardsPercentage || 0),
      tooltip: {
        range:
          bucket.bucketStart === bucket.bucketEnd
            ? dayFormatter.format(start)
            : `${dayFormatter.format(start)} → ${dayFormatter.format(end)}`,
        gm: Number(bucket.totalGMStays || 0),
        real: Number(bucket.totalRealStays || 0),
        actionsCount: Number(bucket.totalStaysWithActions || 0),
        effectiveActionsCount: Number(bucket.totalStaysWithEffectiveActions || 0),
        rewardsCount: Number(bucket.totalStaysWithRewardsUsed || 0),
      },
    };
  });
};

const GENERAL_METRIC_CONFIG = {
  gm: { label: "Séjours GM", color: "#3B9C4F" },
  actions: { label: "Séjours avec action", color: "#2563EB" },
  effectiveActions: { label: "Actions efficaces (€ > 0)", color: "#7C3AED" },
  rewards: { label: "Séjours avec récompense", color: "#F97316" },
};

const DEFAULT_GENERAL_METRICS_VISIBILITY = {
  gm: true,
  actions: true,
  effectiveActions: true,
  rewards: true,
};

const GreenMilesStatsPage = () => {
  const auth = useAuth();
  const token = auth.user?.access_token;
  const {
    hotels,
    loading,
    error,
    featureFilter,
    setFeatureFilter,
    selectedFeatureStatuses,
    availableFeatureStatuses,
    toggleFeatureStatus,
    clearFeatureStatusFilters,
    selectAllFeatureStatuses,
    filteredCount,
    totalCount,
  } = useStatsHotels();

  const normalizeHotelId = useCallback((value) => Number(value), []);

  const [activeTab, setActiveTab] = useState("analyser");
  const [selectedHotels, setSelectedHotels] = useState([]);
  const [startDate, setStartDate] = useState(DEFAULT_DATE_RANGE.startDate);
  const [endDate, setEndDate] = useState(DEFAULT_DATE_RANGE.endDate);
  const [granularity, setGranularity] = useState("month");
  const [activeShortcut, setActiveShortcut] = useState("last12months");
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState(null);
  const [statsData, setStatsData] = useState(null);
  const [activeChartView, setActiveChartView] = useState("general");
  const [generalMetricsVisibility, setGeneralMetricsVisibility] = useState(
    DEFAULT_GENERAL_METRICS_VISIBILITY
  );
  const [economiesMetric, setEconomiesMetric] = useState("euro");
  const [economiesData, setEconomiesData] = useState({ aggregated: null, timeline: [] });
  const [economiesLoading, setEconomiesLoading] = useState(false);
  const [economiesError, setEconomiesError] = useState(null);

  const lastFetchKeyRef = useRef(null);
  const filterSelectionKeyRef = useRef("");

  const effectiveSelectedHotelIds = useMemo(() => {
    const visibleIdSet = new Set(hotels.map((hotel) => normalizeHotelId(hotel.hId)));
    return selectedHotels.filter((id) => visibleIdSet.has(id));
  }, [hotels, selectedHotels, normalizeHotelId]);

  const buildStatsFetchKey = useCallback(
    (hotelIds, start, end, granularityOption) =>
      JSON.stringify({
        hotelIds: [...hotelIds].sort((a, b) => a - b),
        startDate: start,
        endDate: end,
        granularity: granularityOption,
      }),
    []
  );

  const buildStatsPayload = useCallback(
    () => ({
      hotelIds: effectiveSelectedHotelIds,
      start: startDate,
      end: endDate,
      granularityOption: granularity,
    }),
    [effectiveSelectedHotelIds, startDate, endDate, granularity]
  );

  // Aligner la sélection sur la liste visible quand les filtres feature/statut changent
  useEffect(() => {
    if (loading || !hotels.length) {
      return;
    }

    const statusKey = [...selectedFeatureStatuses].sort().join(",");
    const filterKey = `${featureFilter}|${statusKey}`;

    if (filterSelectionKeyRef.current === filterKey) {
      return;
    }

    filterSelectionKeyRef.current = filterKey;
    setSelectedHotels(hotels.map((hotel) => normalizeHotelId(hotel.hId)));
  }, [loading, hotels, featureFilter, selectedFeatureStatuses, normalizeHotelId]);

  const fetchStats = useCallback(
    async ({ hotelIds, start, end, granularityOption }) => {
      if (!token || !hotelIds.length) {
        setStatsData(null);
        return;
      }

      setStatsLoading(true);
      setStatsError(null);

      try {
        const response = await fetch(`${API_URL}/hotels-stats/aggregated`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            hotelIds,
            startDate: start,
            endDate: end,
            granularity: granularityOption,
          }),
        });

        const payload = await response.json().catch(() => ({}));

        if (!response.ok || payload.success !== "OK") {
          throw new Error(payload?.message || `Erreur API (${response.status})`);
        }

        setStatsData(payload.data);
      } catch (err) {
        setStatsError(err.message || "Impossible de récupérer les statistiques.");
        setStatsData(null);
      } finally {
        setStatsLoading(false);
      }
    },
    [token]
  );

  const fetchEconomiesStats = useCallback(
    async ({ hotelIds, start, end, granularityOption }) => {
      if (!token || !hotelIds.length) {
        setEconomiesData({ aggregated: null, timeline: [] });
        return;
      }

      setEconomiesLoading(true);
      setEconomiesError(null);

      try {
        const response = await fetch(`${API_URL}/greenmiles/economies-stats`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            hotelIds,
            startDate: start,
            endDate: end,
            granularity: granularityOption,
          }),
        });

        const payload = await response.json().catch(() => ({}));

        if (!response.ok || payload.success !== "OK") {
          throw new Error(payload?.message || `Erreur API (${response.status})`);
        }

        setEconomiesData(payload.data || { aggregated: null, timeline: [] });
      } catch (err) {
        setEconomiesError(err.message || "Impossible de récupérer les économies.");
        setEconomiesData({ aggregated: null, timeline: [] });
      } finally {
        setEconomiesLoading(false);
      }
    },
    [token]
  );

  const runStatsFetch = useCallback(
    (force = false) => {
      if (!effectiveSelectedHotelIds.length) {
        return;
      }

      const payload = buildStatsPayload();
      const fetchKey = buildStatsFetchKey(
        payload.hotelIds,
        payload.start,
        payload.end,
        payload.granularityOption
      );

      if (!force && lastFetchKeyRef.current === fetchKey) {
        return;
      }

      lastFetchKeyRef.current = fetchKey;
      fetchStats(payload);
      fetchEconomiesStats(payload);
    },
    [
      effectiveSelectedHotelIds.length,
      buildStatsPayload,
      buildStatsFetchKey,
      fetchStats,
      fetchEconomiesStats,
    ]
  );

  useEffect(() => {
    if (!token || loading || !hotels.length || !effectiveSelectedHotelIds.length) {
      return;
    }
    runStatsFetch(false);
  }, [token, loading, hotels.length, effectiveSelectedHotelIds, startDate, endDate, granularity, runStatsFetch]);

  const handleValidate = () => {
    runStatsFetch(true);
  };

  const toggleHotelSelection = (hotelId) => {
    const normalizedId = normalizeHotelId(hotelId);
    setSelectedHotels((prev) =>
      prev.includes(normalizedId)
        ? prev.filter((id) => id !== normalizedId)
        : [...prev, normalizedId]
    );
  };

  const selectAllHotels = () => {
    const visibleIds = hotels.map((hotel) => normalizeHotelId(hotel.hId));
    const allVisibleSelected =
      visibleIds.length > 0 &&
      visibleIds.every((id) => effectiveSelectedHotelIds.includes(id));

    if (allVisibleSelected) {
      setSelectedHotels((prev) => prev.filter((id) => !visibleIds.includes(id)));
      return;
    }

    setSelectedHotels((prev) => [...new Set([...prev, ...visibleIds])]);
  };

  const visibleSelectedCount = effectiveSelectedHotelIds.length;

  const applyShortcut = (shortcutId) => {
    setActiveShortcut(shortcutId);
    switch (shortcutId) {
      case "last12months": {
        const range = getLastMonthsRange(12);
        setStartDate(range.startDate);
        setEndDate(range.endDate);
        break;
      }
      case "last6months": {
        const range = getLastMonthsRange(6);
        setStartDate(range.startDate);
        setEndDate(range.endDate);
        break;
      }
      case "last3months": {
        const range = getLastMonthsRange(3);
        setStartDate(range.startDate);
        setEndDate(range.endDate);
        break;
      }
      default: {
        setStartDate(DEFAULT_DATE_RANGE.startDate);
        setEndDate(DEFAULT_DATE_RANGE.endDate);
      }
    }
  };

  const GranularityButton = ({ id, label }) => (
    <button
      type="button"
      onClick={() => setGranularity(id)}
      className={`px-4 py-2 text-sm font-semibold rounded-full transition ${
        granularity === id ? "bg-white text-[#365644] shadow" : "text-gray-600"
      }`}
    >
      {label}
    </button>
  );

  const ShortcutButton = ({ id, label }) => (
    <button
      type="button"
      onClick={() => applyShortcut(id)}
      className={`px-4 py-2 text-xs font-semibold rounded-full transition ${
        activeShortcut === id ? "bg-[#365644] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
      }`}
    >
      {label}
    </button>
  );

  const aggregated = statsData?.aggregated;
  const timeline = statsData?.timeline;
  const appliedPeriod = aggregated?.period;
  const periodDiffersFromForm =
    Boolean(appliedPeriod) &&
    (appliedPeriod.startDate !== startDate || appliedPeriod.endDate !== endDate);
  const chartData = useMemo(() => {
    const timelineData = buildChartData(timeline);
    if (timelineData.length > 0) {
      return timelineData;
    }

    if (!aggregated) {
      return [];
    }

    const gmValue = Number(aggregated.gmStaysPercentage || 0);
    const actionsValue = Number(aggregated.staysWithActionsPercentage || 0);
    const effectiveActionsValue = Number(aggregated.staysWithEffectiveActionsPercentage || 0);
    const rewardsValue = Number(aggregated.staysWithRewardsPercentage || 0);

    const baseTooltip = {
      gm: Number(aggregated.totalGMStays || 0),
      real: Number(aggregated.totalRealStays || 0),
      actionsCount: Number(aggregated.totalStaysWithActions || 0),
      effectiveActionsCount: Number(aggregated.totalStaysWithEffectiveActions || 0),
      rewardsCount: Number(aggregated.totalStaysWithRewardsUsed || 0),
    };

    const appliedStart = aggregated?.period?.startDate || startDate;
    const appliedEnd = aggregated?.period?.endDate || endDate;
    const start = parseDateInput(appliedStart);
    const end = parseDateInput(appliedEnd);
    const syntheticSeries = [];

    const pushPoint = (rangeStart, rangeEnd) => {
      const label =
        granularity === "day"
          ? dayFormatter.format(rangeStart)
          : granularity === "week"
          ? `Semaine du ${dayFormatter.format(rangeStart)}`
          : monthFormatter.format(rangeStart);

      const tooltipRange =
        rangeStart.getTime() === rangeEnd.getTime()
          ? dayFormatter.format(rangeStart)
          : `${dayFormatter.format(rangeStart)} → ${dayFormatter.format(rangeEnd)}`;

      syntheticSeries.push({
        label,
        gm: gmValue,
        actions: actionsValue,
        effectiveActions: effectiveActionsValue,
        rewards: rewardsValue,
        tooltip: {
          ...baseTooltip,
          range: tooltipRange,
        },
      });
    };

    if (granularity === "day") {
      let current = cloneDate(start);
      while (current.getTime() <= end.getTime()) {
        pushPoint(current, current);
        current.setDate(current.getDate() + 1);
      }
    } else if (granularity === "week") {
      let current = getStartOfISOWeek(start);
      while (current.getTime() <= end.getTime()) {
        const weekStart = maxDate(current, start);
        const weekEnd = minDate(getEndOfISOWeek(current), end);
        pushPoint(weekStart, weekEnd);
        current.setDate(current.getDate() + 7);
      }
    } else {
      let current = new Date(start.getFullYear(), start.getMonth(), 1);
      while (current.getTime() <= end.getTime()) {
        const monthStart = maxDate(current, start);
        const monthEnd = minDate(new Date(current.getFullYear(), current.getMonth() + 1, 0), end);
        pushPoint(monthStart, monthEnd);
        current.setMonth(current.getMonth() + 1);
      }
    }

    return syntheticSeries;
  }, [timeline, aggregated, startDate, endDate, granularity]);
  const hasChartData = chartData.length > 0;

  const visibleGeneralMetricIds = useMemo(
    () => Object.keys(GENERAL_METRIC_CONFIG).filter((id) => generalMetricsVisibility[id]),
    [generalMetricsVisibility]
  );

  const generalYDomain = useMemo(() => {
    if (!chartData.length || visibleGeneralMetricIds.length === 0) {
      return [0, 100];
    }

    let maxValue = 0;
    for (const point of chartData) {
      for (const metricId of visibleGeneralMetricIds) {
        maxValue = Math.max(maxValue, Number(point[metricId] || 0));
      }
    }

    if (maxValue <= 0) {
      return [0, 10];
    }

    const paddedMax = Math.min(100, Math.ceil((maxValue * 1.12) / 5) * 5);
    return [0, Math.max(paddedMax, 5)];
  }, [chartData, visibleGeneralMetricIds]);

  const toggleGeneralMetric = (metricId) => {
    setGeneralMetricsVisibility((prev) => {
      const next = { ...prev, [metricId]: !prev[metricId] };
      const visibleCount = Object.values(next).filter(Boolean).length;
      if (visibleCount === 0) {
        return prev;
      }
      return next;
    });
  };

  const economiesTimeline = economiesData?.timeline || [];
  const economiesAggregated = economiesData?.aggregated;

  const economiesChartData = useMemo(() => {
    return economiesTimeline.map((bucket) => {
      const start = parseDateInput(bucket.bucketStart);
      const end = parseDateInput(bucket.bucketEnd);
      const rangeLabel =
        start.getTime() === end.getTime()
          ? dayFormatter.format(start)
          : `${dayFormatter.format(start)} → ${dayFormatter.format(end)}`;

      return {
        label:
          granularity === "month"
            ? monthFormatter.format(start)
            : granularity === "week"
            ? dayFormatter.format(start)
            : dayFormatter.format(start),
        euros: Number(bucket.totalEuroSavings || 0),
        water: Number(bucket.totalWaterSavings || 0),
        co2: Number(bucket.totalCO2Savings || 0),
        tooltipRange: rangeLabel,
      };
    });
  }, [economiesTimeline, granularity]);

  const economyMetricConfig = {
    euro: { label: "Euros (€/chambre)", key: "euros", color: "#3B9C4F", unit: "€/chambre" },
    water: { label: "Eau (L/chambre)", key: "water", color: "#2563EB", unit: "L/chambre" },
    co2: { label: "CO2 (kg/chambre)", key: "co2", color: "#F97316", unit: "kg/chambre" },
  };

  if (error && !hotels.length) {
    return <ErrorState error={error} />;
  }

  const isInitialHotelsLoading = loading && hotels.length === 0;

  return (
    <div className="flex h-screen bg-[#F2F3EF]">
      <Sidebar />
      <main className="flex-1 flex flex-col overflow-y-auto px-12 py-8 space-y-8">
        <header className="flex flex-col">
          <h1 className="text-3xl font-bold text-gray-900">Statistiques</h1>
        </header>

        {isInitialHotelsLoading ? (
          <div className="flex flex-1 items-center justify-center py-24">
            <LoadingState message="Chargement des hôtels…" embedded />
          </div>
        ) : (
          <>
            {error ? <ErrorState error={error} embedded /> : null}

            <section className="bg-white rounded-3xl shadow-sm border border-gray-200 px-8 py-6 space-y-6">
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => setActiveTab("analyser")}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition ${
                activeTab === "analyser" ? "bg-[#365644] text-white shadow" : "bg-gray-100 text-gray-600"
              }`}
            >
              Analyser
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("comparer")}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition ${
                activeTab === "comparer" ? "bg-[#365644] text-white shadow" : "bg-gray-100 text-gray-600"
              }`}
            >
              Comparer
            </button>
          </div>

          <div className="grid gap-6 lg:grid-cols-[1.5fr_1.5fr_1.2fr]">
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-semibold text-gray-800">Hôtels*</span>
                <button type="button" onClick={selectAllHotels} className="text-xs font-semibold text-[#365644] shrink-0">
                  {visibleSelectedCount === filteredCount && filteredCount > 0
                    ? "Tout désélectionner"
                    : "Tout sélectionner"}
                </button>
              </div>
              <div className="inline-flex rounded-full bg-gray-100 border border-gray-200 p-1 w-full">
                {STATS_FEATURE_FILTERS.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => setFeatureFilter(option.id)}
                    className={`flex-1 px-2 py-1.5 text-xs font-semibold rounded-full transition ${
                      featureFilter === option.id ? "bg-white text-[#365644] shadow-sm" : "text-gray-600"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              {featureFilter !== "all" && (
                <FeatureStatusFilter
                  availableStatuses={availableFeatureStatuses}
                  selectedStatuses={selectedFeatureStatuses}
                  onStatusToggle={toggleFeatureStatus}
                  onSelectAll={selectAllFeatureStatuses}
                  onClearAll={clearFeatureStatusFilters}
                />
              )}
              <p className="text-xs text-gray-500">
                {filteredCount} hôtel{filteredCount > 1 ? "s" : ""} affiché
                {filteredCount !== totalCount ? ` sur ${totalCount}` : ""}
                {visibleSelectedCount > 0 ? ` · ${visibleSelectedCount} sélectionné${visibleSelectedCount > 1 ? "s" : ""}` : ""}
              </p>
              <div className="border border-gray-200 rounded-2xl p-3 space-y-3 max-h-48 overflow-y-auto">
                {filteredCount === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">Aucun hôtel ne correspond aux filtres.</p>
                ) : (
                  hotels.map((hotel) => {
                    const normalizedId = normalizeHotelId(hotel.hId);
                    const statusForBadge =
                      featureFilter === "lunishower"
                        ? hotel.lunishower_status
                        : featureFilter === "greenmiles"
                          ? hotel.greenmiles_status
                          : null;
                    return (
                      <label
                        key={hotel.hId}
                        className={`flex items-center justify-between gap-2 rounded-xl border px-3 py-2 text-sm transition ${
                          selectedHotels.includes(normalizedId)
                            ? "bg-[#365644]/10 border-[#365644]"
                            : "bg-white border-gray-200 hover:border-[#365644]/40"
                        }`}
                      >
                        <span className="font-medium text-gray-800 min-w-0 truncate">{hotel.hName}</span>
                        <span className="flex items-center gap-2 shrink-0">
                          {featureFilter !== "all" && <FeatureStatusBadge status={statusForBadge} />}
                          <input
                            type="checkbox"
                            checked={selectedHotels.includes(normalizedId)}
                            onChange={() => toggleHotelSelection(normalizedId)}
                            className="form-checkbox h-4 w-4 text-[#365644]"
                          />
                        </span>
                      </label>
                    );
                  })
                )}
              </div>
            </div>

            <div className="space-y-5">
              <div className="space-y-2">
                <span className="text-sm font-semibold text-gray-800">Période d’analyse*</span>
                <div className="flex items-center space-x-3">
                  <input
                    type="date"
                    value={startDate}
                    onChange={(event) => setStartDate(event.target.value)}
                    className="flex-1 rounded-xl border border-gray-300 px-3 py-2 text-sm"
                  />
                  <span className="text-sm text-gray-500">→</span>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(event) => setEndDate(event.target.value)}
                    className="flex-1 rounded-xl border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <span className="text-sm font-semibold text-gray-800">Raccourcis</span>
                <div className="flex flex-wrap gap-2">
                  <ShortcutButton id="last12months" label="12 derniers mois" />
                  <ShortcutButton id="last6months" label="6 derniers mois" />
                  <ShortcutButton id="last3months" label="3 derniers mois" />
                </div>
              </div>
            </div>

            <div className="space-y-5">
              <div className="space-y-2">
                <span className="text-sm font-semibold text-gray-800">Granularité</span>
                <div className="inline-flex rounded-full bg-gray-100 border border-gray-200 p-1">
                  <GranularityButton id="day" label="Jour" />
                  <GranularityButton id="week" label="Semaine" />
                  <GranularityButton id="month" label="Mois" />
                </div>
              </div>
              <button
                type="button"
                onClick={handleValidate}
                disabled={statsLoading || !effectiveSelectedHotelIds.length}
                className="w-full rounded-full bg-[#365644] text-white text-sm font-semibold py-3 shadow hover:bg-[#2b4234] transition"
              >
                {statsLoading ? "Analyse en cours..." : "Valider"}
              </button>
              {appliedPeriod && !periodDiffersFromForm && !statsLoading && (
                <p className="text-xs text-gray-500">
                  Période appliquée : {dayFormatter.format(parseDateInput(appliedPeriod.startDate))} →{" "}
                  {dayFormatter.format(parseDateInput(appliedPeriod.endDate))}
                </p>
              )}
              {periodDiffersFromForm && !statsLoading && (
                <p className="text-xs text-amber-700">
                  La période affichée dans les graphiques correspond encore à{" "}
                  {dayFormatter.format(parseDateInput(appliedPeriod.startDate))} →{" "}
                  {dayFormatter.format(parseDateInput(appliedPeriod.endDate))}. Cliquez sur Valider ou
                  attendez le recalcul automatique.
                </p>
              )}
              {statsError && <p className="text-xs text-red-500">{statsError}</p>}
            </div>
          </div>
        </section>

        <section className="space-y-6">
          <div className="bg-white rounded-3xl shadow-sm border border-gray-200 p-6 space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4 text-sm font-semibold text-gray-600">
                <button
                  type="button"
                  onClick={() => setActiveChartView("general")}
                  className={`px-4 py-2 rounded-full ${activeChartView === "general" ? "bg-[#365644] text-white shadow" : "bg-gray-100 text-gray-500"}`}
                >
                  Général
                </button>
                <button
                  type="button"
                  onClick={() => setActiveChartView("economies")}
                  className={`px-4 py-2 rounded-full ${activeChartView === "economies" ? "bg-[#365644] text-white shadow" : "bg-gray-100 text-gray-500"}`}
                >
                  Économies
                </button>
              </div>
              <div className="text-sm font-semibold text-gray-600 capitalize">
                Par {granularity === "day" ? "jour" : granularity === "week" ? "semaine" : "mois"}
              </div>
            </div>
            <div className="h-80">
              {activeChartView === "general" ? (
                hasChartData && visibleGeneralMetricIds.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData} margin={{ top: 20, right: 10, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="label" stroke="#94A3B8" />
                      <YAxis
                        domain={generalYDomain}
                        stroke="#94A3B8"
                        tickFormatter={(value) => `${value}%`}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      {Object.entries(GENERAL_METRIC_CONFIG).map(([metricId, config]) => (
                        <Line
                          key={metricId}
                          type="monotone"
                          dataKey={metricId}
                          stroke={config.color}
                          strokeWidth={3}
                          dot={false}
                          hide={!generalMetricsVisibility[metricId]}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                ) : hasChartData ? (
                  <div className="h-full flex items-center justify-center text-sm text-gray-500">
                    Sélectionnez au moins une métrique à afficher.
                  </div>
                ) : (
                  <div className="h-full flex items-center justify-center text-sm text-gray-500">
                    Aucune donnée disponible pour la période sélectionnée.
                  </div>
                )
              ) : economiesLoading ? (
                <div className="h-full flex items-center justify-center text-sm text-gray-500">
                  Chargement des économies...
                </div>               ) : economiesError ? (                <div className="h-full flex items-center justify-center text-sm text-red-500">
                  {economiesError}
                </div>
              ) : economiesChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={economiesChartData} margin={{ top: 20, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="label" stroke="#94A3B8" />
                    <YAxis stroke="#94A3B8" tickFormatter={(value) => `${Math.round(value)}`} />
                    <Tooltip
                      contentStyle={{ borderRadius: 16, borderColor: "#E2E8F0" }}
                      formatter={(value) => {
                        const config = economyMetricConfig[economiesMetric];
                        return [`${formatNumber(value || 0, 3)} ${config.unit}`, config.label];
                      }}
                      labelFormatter={(label, payload) =>
                        payload?.[0]?.payload?.tooltipRange ? payload[0].payload.tooltipRange : label
                      }
                    />
                    <Bar
                      dataKey={economyMetricConfig[economiesMetric].key}
                      fill={economyMetricConfig[economiesMetric].color}
                      radius={[8, 8, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-sm text-gray-500">
                  Aucune donnée d’économies pour la période sélectionnée.
                </div>
              )}
            </div>
            {activeChartView === "general" && hasChartData && (
              <div className="flex flex-wrap items-center justify-end gap-3">
                {Object.entries(GENERAL_METRIC_CONFIG).map(([metricId, config]) => {
                  const isVisible = generalMetricsVisibility[metricId];
                  return (
                    <button
                      key={metricId}
                      type="button"
                      onClick={() => toggleGeneralMetric(metricId)}
                      className={`px-5 py-2 rounded-full text-sm font-semibold border transition ${
                        isVisible
                          ? "bg-white shadow border-transparent text-[#365644]"
                          : "bg-gray-100 border-gray-200 text-gray-500 opacity-60"
                      }`}
                      style={{ borderColor: isVisible ? config.color : undefined }}
                      aria-pressed={isVisible}
                    >
                      {config.label}
                    </button>
                  );
                })}
              </div>
            )}
            {activeChartView === "economies" && economiesChartData.length > 0 && (
              <div className="flex items-center justify-end gap-3">
                {Object.entries(economyMetricConfig).map(([id, config]) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setEconomiesMetric(id)}
                    className={`px-5 py-2 rounded-full text-sm font-semibold border ${
                      economiesMetric === id
                        ? "bg-white shadow border-transparent text-[#365644]"
                        : "bg-gray-100 border-gray-200 text-gray-600"
                    }`}
                    style={{ borderColor: economiesMetric === id ? config.color : undefined }}
                  >
                    {config.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </section>

        {/*
        <section className="bg-white rounded-3xl shadow-sm border border-gray-200 p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Logs de calcul</h2>
          <div className="grid gap-3 md:grid-cols-2 text-sm text-gray-700">
            <div>
              <span className="font-semibold">Granularité :</span> {granularity}
            </div>
            <div>
              <span className="font-semibold">Hôtels sélectionnés :</span> {effectiveSelectedHotelIds.length}
            </div>
            <div>
              <span className="font-semibold">Période :</span> {dayFormatter.format(parseDateInput(startDate))} →{" "}
              {dayFormatter.format(parseDateInput(endDate))}
            </div>
            {aggregated && (
              <>
                <div>
                  <span className="font-semibold">Séjours GM :</span>{" "}
                  {formatNumber(aggregated.totalGMStays)} / {formatNumber(aggregated.totalRealStays)} (
                  {percentageFormatter.format(aggregated.gmStaysPercentage || 0)} %)
                </div>
                <div>
                  <span className="font-semibold">Séjours GM avec action :</span>{" "}
                  {formatNumber(aggregated.totalStaysWithActions)} (
                  {percentageFormatter.format(aggregated.staysWithActionsPercentage || 0)} %)
                </div>
                <div>
                  <span className="font-semibold">Séjours GM avec récompense :</span>{" "}
                  {formatNumber(aggregated.totalStaysWithRewardsUsed)} (
                  {percentageFormatter.format(aggregated.staysWithRewardsPercentage || 0)} %)
                </div>
              </>
            )}
          </div>

          {timeline?.buckets?.length ? (
            <details className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-sm text-gray-700">
              <summary className="cursor-pointer font-semibold text-gray-900">
                Timeline ({timeline.buckets.length} points)
              </summary>
              <div className="mt-3 max-h-64 overflow-auto">
                <table className="w-full text-left text-xs">
                  <thead className="text-gray-500 uppercase tracking-wide">
                    <tr>
                      <th className="py-1 pr-4">Période</th>
                      <th className="py-1 pr-4 text-right">% GM</th>
                      <th className="py-1 pr-4 text-right">% Actions</th>
                      <th className="py-1 pr-4 text-right">% Récompenses</th>
                      <th className="py-1 pr-4 text-right">GM</th>
                      <th className="py-1 pr-4 text-right">Réels</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-600">
                    {timeline.buckets.map((bucket, index) => {
                      const start = dayFormatter.format(parseDateInput(bucket.bucketStart));
                      const end = dayFormatter.format(parseDateInput(bucket.bucketEnd));
                      const period = start === end ? start : `${start} → ${end}`;
                      return (
                        <tr
                          key={`${bucket.bucketStart}-${index}`}
                          className={index % 2 === 0 ? "bg-white" : "bg-gray-100/60"}
                        >
                          <td className="py-1 pr-4 whitespace-nowrap">{period}</td>
                          <td className="py-1 pr-4 text-right">
                            {percentageFormatter.format(bucket.gmStaysPercentage || 0)} %
                          </td>
                          <td className="py-1 pr-4 text-right">
                            {percentageFormatter.format(bucket.staysWithActionsPercentage || 0)} %
                          </td>
                          <td className="py-1 pr-4 text-right">
                            {percentageFormatter.format(bucket.staysWithRewardsPercentage || 0)} %
                          </td>
                          <td className="py-1 pr-4 text-right">{formatNumber(bucket.totalGMStays)}</td>
                          <td className="py-1 pr-4 text-right">{formatNumber(bucket.totalRealStays)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </details>
          ) : (
            <div className="text-sm text-gray-500">
              Aucune timeline n’a été renvoyée par l’API pour ces paramètres.
            </div>
          )}

          {activeChartView === "economies" && (
            <>
              <h3 className="text-lg font-semibold text-gray-900">Logs économies</h3>
              {economiesAggregated ? (
                <div className="grid gap-3 md:grid-cols-3 text-sm text-gray-700">
                  <div>
                    <span className="font-semibold">Total euros :</span> {formatNumber(economiesAggregated.totalEuroSavings, 3)} €
                  </div>
                  <div>
                    <span className="font-semibold">Total eau :</span> {formatNumber(economiesAggregated.totalWaterSavings, 3)} L
                  </div>
                    <span className="font-semibold">Total CO₂ :</span> {formatNumber(economiesAggregated.totalCO2Savings, 3)} kg
                  </div>
                </div>
              ) : (
                <div className="text-sm text-gray-500">Aucune économie calculée pour ces paramètres.</div>
              )}
            </>
          )}
        </section>
        */}
          </>
        )}
      </main>
    </div>
  );
};

export default GreenMilesStatsPage;
